import React, { useState, useRef, useEffect } from "react";
import { FileDrop } from "./FileDrop";
import { Text } from "./Text";
import { Popup, Icon } from "semantic-ui-react";

type FinetuningFileUploadProps = {
  inputId?: string;
  acceptedFormats?: string;
};

export const FinetuningFileUpload: React.FC<FinetuningFileUploadProps> = ({
  inputId = "id_uploaded_file",
  acceptedFormats = ".csv,.xlsx,.json,.xml,.tmx",
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [syncError, setSyncError] = useState(false);
  const hiddenInputRef = useRef<HTMLInputElement | null>(null);

  const assignFilesToInput = (
    input: HTMLInputElement,
    files: FileList
  ): boolean => {
    const descriptor = Object.getOwnPropertyDescriptor(
      Object.getPrototypeOf(input),
      "files"
    );
    if (descriptor?.set) {
      descriptor.set.call(input, files);
      return true;
    }
    return false;
  };

  // Find the hidden Django form input and sync file selection
  useEffect(() => {
    const input = document.getElementById(inputId) as HTMLInputElement;
    if (input) {
      hiddenInputRef.current = input;
    }
  }, [inputId]);

  // Sync selected file to hidden input
  useEffect(() => {
    const hiddenInput = hiddenInputRef.current;
    if (!hiddenInput) {
      return;
    }

    if (selectedFile) {
      if (typeof DataTransfer === "undefined") {
        console.warn("DataTransfer is not supported in this browser");
        // Cannot clear file input without DataTransfer support
        setSyncError(true);
        return;
      }
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(selectedFile);
      const success = assignFilesToInput(hiddenInput, dataTransfer.files);
      if (!success) {
        console.warn("Unable to assign files to input element");
        // Clear using empty DataTransfer
        const emptyDataTransfer = new DataTransfer();
        assignFilesToInput(hiddenInput, emptyDataTransfer.files);
        setSyncError(true);
        return;
      }
      setSyncError(false);
    } else {
      // Clear the input when no file is selected
      if (typeof DataTransfer !== "undefined") {
        const emptyDataTransfer = new DataTransfer();
        assignFilesToInput(hiddenInput, emptyDataTransfer.files);
      }
      setSyncError(false);
    }

    const event = new Event("change", { bubbles: true });
    hiddenInput.dispatchEvent(event);
  }, [selectedFile]);

  const handleFileChange = (file: File | null): void => {
    setSelectedFile(file);
  };

  const isValidFile = (file: File | null): boolean => {
    if (!file) return true;
    const extension = "." + file.name.split(".").pop()?.toLowerCase();
    const accepted = acceptedFormats
      .split(",")
      .map((f) => f.trim().toLowerCase());
    return accepted.includes(extension);
  };

  const isInvalid = selectedFile ? !isValidFile(selectedFile) : false;

  return (
    <div className="tw-relative">
      <div className="tw-flex tw-items-start tw-gap-2">
        <div className="tw-max-w-lg">
          <style>{`
            .finetuning-file-drop .file-drop-target {
              margin-left: 0 !important;
              margin-right: 0 !important;
            }
            .finetuning-file-drop [data-testid="file-drop-target-wrapper"] {
              display: flex;
              flex-direction: column;
              align-items: center;
            }
          `}</style>
          <div className="finetuning-file-drop">
            <FileDrop
              selectedFile={selectedFile}
              isInvalid={isInvalid}
              onChange={handleFileChange}
              contentUnselected={
                <>
                  <Text>
                    Accepted formats include CSV, Excel (.xlsx), JSON, XML, TMX.
                  </Text>
                </>
              }
            />
          </div>
        </div>
        <div className="tw-pt-2">
          <Popup
            wide="very"
            trigger={
              <Icon
                name="question circle"
                className="tw-cursor-pointer tw-text-gray-500"
                style={{ fontSize: "1.2em" }}
              />
            }
            hoverable
            position="bottom left"
          >
            <Popup.Content>
              <div className="tw-text-sm">
                <p className="tw-font-semibold tw-mb-2">
                  File Format Instructions
                </p>
                <p className="tw-mb-2">
                  File should contain a list of input/output examples, along
                  with an optional instruction:
                </p>
                <table className="txu-table txu-compact tw-mt-2">
                  <thead>
                    <tr>
                      <th>Instruction</th>
                      <th>Input</th>
                      <th>Output</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Translate to Swedish</td>
                      <td>High Neck Vest Top</td>
                      <td>Västtopp med hög hals</td>
                    </tr>
                    <tr>
                      <td>Write a marketing sentence with these keywords</td>
                      <td>Elastic Waist Midi Skirt</td>
                      <td>
                        Slip into effortless style with this Midi Skirt,
                        featuring an elastic waist for all-day comfort …
                      </td>
                    </tr>
                    <tr>
                      <td>⋮</td>
                      <td>⋮</td>
                      <td>⋮</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Popup.Content>
          </Popup>
          {syncError && (
            <Text color="red" className="tw-mt-2">
              Drag & drop is not supported in this browser. Please use the
              “Select file to upload” button instead.
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};
